import { formatUsPhoneDisplay } from './phoneUtils';

/**
 * Centralized formatting utilities.
 *
 * Rules:
 *  - Dollar amounts: 2 decimal places  (e.g. $250,000.00)
 *  - Pricing-related dollars (price, points, lender credits): 3 decimal places
 *  - Ratios (DTI, etc.): 2 decimal places  (e.g. 43.25%)
 *  - LTV / CLTV / HCLTV: 2 decimal places  (e.g. 80.00%)
 *  - Rates / pricing percentages: 3 decimal places (e.g. 6.500%)
 */

/** Standard dollar — 2 decimal places */
export const fmtDollar = (v) =>
  v != null
    ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

/** Pricing dollar — 3 decimal places (price, points, lender credits) */
export const fmtPricingDollar = (v) =>
  v != null
    ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`
    : '—';

/** Standard percentage / ratio — 2 decimal places (DTI, LTV, CLTV, HCLTV) */
export const fmtPct = (v) =>
  v != null ? `${Number(v).toFixed(2)}%` : '—';

/** Pricing percentage — 3 decimal places (interest rate, price, margin) */
export const fmtPricingPct = (v) =>
  v != null ? `${Number(v).toFixed(3)}%` : '—';

/** Alias — interest rate display (same as fmtPricingPct). */
export const fmtRate = fmtPricingPct;

/** Alias — pricing/price display (same as fmtPricingDollar). */
export const fmtPrice = fmtPricingDollar;

/** Plain number with commas, no currency symbol */
export const fmtNum = (v) =>
  v != null ? Number(v).toLocaleString() : '—';

/** Date — locale short format */
export const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString() : '—';

/** Short date — e.g. "Aug 1, 2026" (pipeline tables, dashboards). */
export const fmtShortDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Month + day only, no year — e.g. "Aug 1" (compact pipeline rows/captions). */
export const fmtMonthDay = (v) => {
  if (v == null) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Numeric MM/DD/YYYY date — e.g. "08/01/2026" (loan file forms). */
export const fmtDateMDY = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

/** Compact dollar display — e.g. "$1.2M", "$450K" (dashboard tiles/rows). */
export const fmtDollarCompact = (v) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};

/** Phone — formats a digit string or partially formatted number to (XXX) XXX-XXXX */
export const fmtPhone = (v) => {
  if (!v) return '—';
  const digits = String(v).replace(/\D/g, '');
  if (digits.length === 0) return '—';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

/** US-style phone as you type — for controlled inputs (max 10 national digits). */
export const formatPhoneInputValue = (value) => formatUsPhoneDisplay(value);
